/**
 * Panel de Configuración de Destinatarios para Alertas.
 * Permite a los administradores gestionar la lista de correos electrónicos que recibirán
 * notificaciones automáticas sobre riesgos epidemiológicos y climáticos detectados.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Apiurl } from '../../shared/services/apirest';
import bootstrap from "bootstrap/dist/js/bootstrap.bundle";

const AdminNotificationSettings = () => {
    const [recipients, setRecipients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingRecipient, setEditingRecipient] = useState(null);
    const [formData, setFormData] = useState({
        role: 'ADMIN_LAB',
        email: '',
        is_active: true
    });

    const modalRef = useRef(null);
    const token = localStorage.getItem('token');
    const currentUserRole = localStorage.getItem('role');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    /**
     * Obtiene la lista de destinatarios de alertas desde el servidor.
     */
    const fetchRecipients = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${Apiurl}casos/destinatarios-alertas/`, config);
            setRecipients(res.data);
        } catch (error) {
            console.error("Error fetching alert recipients:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRecipients();
    }, [fetchRecipients]);

    /**
     * Filtra los destinatarios visibles según el rol del usuario actual.
     */
    const filteredRecipients = recipients.filter(r => {
        if (currentUserRole === 'admin') return true;
        if (currentUserRole === 'admin_lab') return r.role === 'ADMIN_LAB';
        if (currentUserRole === 'admin_investigacion') return r.role === 'ADMIN_INVESTIGACION';
        return false;
    });

    /**
     * Abre el modal para añadir o editar un destinatario.
     */
    const handleOpenModal = (recipient = null) => {
        if (recipient) {
            setEditingRecipient(recipient);
            setFormData({
                role: recipient.role,
                email: recipient.email,
                is_active: recipient.is_active
            });
        } else {
            setEditingRecipient(null);

            // Establecer rol por defecto según el rol del usuario actual
            let defaultRole = 'ADMIN_LAB';
            if (currentUserRole === 'admin_investigacion') {
                defaultRole = 'ADMIN_INVESTIGACION';
            }

            setFormData({
                role: defaultRole,
                email: '',
                is_active: true
            });
        }
        const modal = new bootstrap.Modal(modalRef.current);
        modal.show();
    };

    /**
     * Cierra el modal de edición/creación.
     */
    const handleCloseModal = () => {
        const modal = bootstrap.Modal.getInstance(modalRef.current);
        if (modal) modal.hide();
    };

    /**
     * Procesa el envío del formulario para guardar o actualizar un destinatario.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingRecipient) {
                await axios.put(`${Apiurl}casos/destinatarios-alertas/${editingRecipient.id}/`, formData, config);
            } else {
                await axios.post(`${Apiurl}casos/destinatarios-alertas/`, formData, config);
            }
            fetchRecipients();
            handleCloseModal();
        } catch (error) {
            console.error("Error saving recipient:", error);
            const msg = error.response?.data?.detail
                || (typeof error.response?.data === 'object' ? JSON.stringify(error.response.data) : null)
                || "Error al guardar el destinatario. Verifique los datos.";
            alert(msg);
        }
    };

    /**
     * Elimina un destinatario de la lista de alertas.
     */
    const handleDelete = async (id) => {
        if (window.confirm("¿Está seguro de eliminar este destinatario?")) {
            try {
                await axios.delete(`${Apiurl}casos/destinatarios-alertas/${id}/`, config);
                fetchRecipients();
            } catch (error) {
                console.error("Error deleting recipient:", error);
            }
        }
    };

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="fw-bold">Configuración de Notificaciones</h2>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                    <i className="bi bi-plus-lg me-2"></i> Añadir Destinatario
                </button>
            </div>

            <p className="text-muted mb-4">
                Configure los correos electrónicos que recibirán alertas epidemiológicas y climáticas.
                {currentUserRole === 'admin'
                    ? " El superadministrador es el encargado de suministrar estos correos para los otros administradores."
                    : " Usted puede gestionar los correos destinados a recibir alertas para su área correspondiente."}
            </p>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                </div>
            ) : (
                <div className="table-responsive bg-white rounded shadow-sm">
                    <table className="table hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>Rol Administrativo</th>
                                <th>Correo Electrónico</th>
                                <th>Estado</th>
                                <th className="text-end">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRecipients.length > 0 ? filteredRecipients.map(r => (
                                <tr key={r.id}>
                                    <td>
                                        <span className="fw-bold">{r.role_display}</span>
                                    </td>
                                    <td>{r.email}</td>
                                    <td>
                                        <span className={`badge bg-${r.is_active ? "success" : "secondary"}`}>
                                            {r.is_active ? "Activo" : "Inactivo"}
                                        </span>
                                    </td>
                                    <td className="text-end">
                                        <button className="btn btn-outline-primary btn-sm me-2" onClick={() => handleOpenModal(r)} title="Editar">
                                            <i className="bi bi-pencil"></i>
                                        </button>
                                        <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(r.id)} title="Eliminar">
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="4" className="text-center py-4 text-muted">
                                        No hay destinatarios configurados.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal para Gestión de Destinatarios */}
            <div className="modal fade" ref={modalRef} tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{editingRecipient ? 'Editar Destinatario' : 'Añadir Destinatario'}</h5>
                            <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Rol Administrativo</label>
                                    <select
                                        className="form-select"
                                        value={formData.role}
                                        onChange={e => setFormData({ ...formData, role: e.target.value })}
                                        disabled={currentUserRole !== 'admin'}
                                    >
                                        {(currentUserRole === 'admin' || currentUserRole === 'admin_lab') && (
                                            <option value="ADMIN_LAB">Administrador de Laboratorio</option>
                                        )}
                                        {(currentUserRole === 'admin' || currentUserRole === 'admin_investigacion') && (
                                            <option value="ADMIN_INVESTIGACION">Administrador de Investigación</option>
                                        )}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Correo Electrónico</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        required
                                        placeholder="ejemplo@correo.com"
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </div>
                                <div className="mb-3 form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="isActiveCheck"
                                        checked={formData.is_active}
                                        onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                                    />
                                    <label className="form-check-label" htmlFor="isActiveCheck">
                                        Habilitar recepción de alertas
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancelar</button>
                                <button type="submit" className="btn btn-primary">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminNotificationSettings;
